import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, 200, 500, 1000, 2000];

export default function RemindersTab({ t }) {
  const [reminders, setReminders] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('reminders'); // 'reminders' | 'rules'

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const searchTimer = useRef(null);

  // Reschedule modal
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  // Rule editing
  const [editingRule, setEditingRule] = useState(null);

  const rm = t.admin?.reminders || {};
  const pg = t.admin?.search || {};
  const formatDate = (date) => { if (!date) return ''; const dateOnly = String(date).substring(0, 10); return new Date(dateOnly + 'T12:00:00').toLocaleDateString(); };
  const formatDateTime = (dt) => dt ? new Date(dt).toLocaleString() : '';

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchDebounced(val);
      setPage(1);
    }, 200);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchDebounced) params.set('q', searchDebounced);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const res = await axios.get(`${API}/admin/reminders?${params}`);
      const data = res.data?.data || [];
      setReminders(data);
      if (res.data?.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
        setTotal(res.data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Load reminders error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchDebounced, statusFilter, page, pageSize]);

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/reminders/rules`);
      setRules(res.data || []);
    } catch (err) {
      console.error('Load rules error:', err);
    }
  }, []);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Reset page to 1 when statusFilter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handleCancel = async (id) => {
    if (!window.confirm(rm.confirmCancel || 'Cancel this reminder?')) return;
    try {
      await axios.patch(`${API}/admin/reminders/${id}/cancel`);
      loadReminders();
    } catch (err) {
      alert(rm.cancelError || 'Failed to cancel reminder');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleModal || !rescheduleDate) return;
    try {
      await axios.patch(`${API}/admin/reminders/${rescheduleModal.id}`, {
        remind_at: new Date(rescheduleDate + 'T09:00:00').toISOString()
      });
      setRescheduleModal(null);
      loadReminders();
    } catch (err) {
      alert(rm.rescheduleError || 'Failed to reschedule');
    }
  };

  const handleSaveRule = async (serviceId) => {
    if (!editingRule) return;
    try {
      await axios.put(`${API}/admin/reminders/rules/${serviceId}`, editingRule);
      setEditingRule(null);
      loadRules();
    } catch (err) {
      alert(rm.saveRuleError || 'Failed to save rule');
    }
  };

  const statusBadge = (status) => {
    const colors = { scheduled: '#3b82f6', processing: '#f97316', sent: '#22c55e', cancelled: '#64748B', failed: '#ef4444' };
    return { display: 'inline-block', padding: '0.15rem 0.5rem', fontSize: '0.7rem', borderRadius: '3px',
      background: `${colors[status] || '#94A3B8'}22`, color: colors[status] || '#94A3B8' };
  };

  // Pagination display
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div>
      <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '2rem' }}>
        {rm.title || 'Maintenance Reminders'}
      </h2>

      {/* View toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button
          className={`btn btn-sm ${activeView === 'reminders' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setActiveView('reminders')}
        >
          {rm.allReminders || 'All Reminders'} ({total})
        </button>
        <button
          className={`btn btn-sm ${activeView === 'rules' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setActiveView('rules')}
        >
          {rm.serviceRules || 'Service Rules'} ({rules.length})
        </button>
      </div>

      {/* REMINDERS VIEW */}
      {activeView === 'reminders' && (
        <div>
          {/* Search + filter */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 250px', maxWidth: '350px' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t.admin.search?.placeholder || 'Search...'}
                value={search}
                onChange={e => handleSearch(e.target.value)}
                style={{ paddingRight: '2rem' }}
              />
              {search && (
                <button onClick={() => { setSearch(''); setSearchDebounced(''); setPage(1); }} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer' }}>&times;</button>
              )}
            </div>
            <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="">{rm.allStatuses || 'All statuses'}</option>
              <option value="scheduled">{rm.scheduled || 'Scheduled'}</option>
              <option value="sent">{rm.sent || 'Sent'}</option>
              <option value="failed">{rm.failed || 'Failed'}</option>
              <option value="cancelled">{rm.cancelled || 'Cancelled'}</option>
            </select>
            {!loading && total > 0 && (
              <span style={{ fontSize: '0.85rem', color: '#94A3B8', marginLeft: 'auto' }}>
                {(pg.showing || '{from}\u2013{to} of {total}').replace('{from}', from).replace('{to}', to).replace('{total}', total)}
              </span>
            )}
          </div>

          {reminders.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{rm.noReminders || 'No reminders'}</div>
          ) : (
            <div className="table-responsive" style={{ position: 'relative', minHeight: '200px' }}>
              {loading && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(12,18,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                  <span style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{t.common?.loading || 'Loading...'}</span>
                </div>
              )}
              <table className="table table-dark table-hover" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>{rm.customer || 'Customer'}</th>
                    <th>{rm.service || 'Service'}</th>
                    <th>{rm.reminderDate || 'Reminder Date'}</th>
                    <th>{rm.channels || 'Channels'}</th>
                    <th>{rm.status || 'Status'}</th>
                    <th>{rm.actions || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.map(r => (
                    <tr key={r.id}>
                      <td style={{ color: '#E8ECF0' }}>{r.first_name} {r.last_name}</td>
                      <td>{r.service_name_fr || r.service_name}</td>
                      <td>{formatDateTime(r.remind_at)}</td>
                      <td>
                        {r.channel_email && <span style={{ marginRight: '0.5rem', fontSize: '0.75rem', color: '#94A3B8' }}>Email</span>}
                        {r.channel_sms && <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>SMS</span>}
                      </td>
                      <td>
                        <span style={statusBadge(r.status)}>{r.status}</span>
                        {r.last_error && <div style={{ fontSize: '0.65rem', color: '#ef4444', marginTop: '0.2rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.last_error}>{r.last_error}</div>}
                      </td>
                      <td>
                        {r.status === 'scheduled' && (
                          <div style={{ display: 'flex', gap: '0.3rem' }}>
                            <button className="btn btn-outline-primary btn-sm" onClick={() => { setRescheduleModal(r); setRescheduleDate(r.remind_at?.substring(0, 10) || ''); }} style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>
                              {rm.reschedule || 'Reschedule'}
                            </button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleCancel(r.id)} style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>
                              {rm.cancel || 'Cancel'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination controls */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            {totalPages > 1 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                {pg.prev || '\u2039 Previous'}
              </button>
            )}
            {totalPages > 1 && (
              <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>
                Page {page} / {totalPages}
              </span>
            )}
            {totalPages > 1 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {pg.next || 'Next \u203A'}
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label style={{ color: '#94A3B8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{pg.rowsPerPage || 'Rows:'}</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                style={{ width: 'auto', minWidth: '70px' }}
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* RULES VIEW */}
      {activeView === 'rules' && (
        <div>
          <p style={{ fontSize: '0.85rem', color: '#94A3B8', marginBottom: '1rem' }}>
            {rm.rulesDescription || 'Configure default reminder intervals for each service. These defaults are suggested when completing appointments.'}
          </p>
          {rules.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{rm.noRules || 'No rules configured. Rules are auto-created when you set defaults for a service.'}</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {rules.map(rule => (
                <div key={rule.id} style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ color: '#E8ECF0', fontSize: '1rem' }}>{rule.service_name_fr || rule.service_name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ color: rule.enabled ? '#22c55e' : '#64748B', fontSize: '0.8rem' }}>
                        {rule.enabled ? (rm.enabled || 'Enabled') : (rm.disabled || 'Disabled')}
                      </span>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setEditingRule(editingRule?.service_id === rule.service_id ? null : {
                          service_id: rule.service_id,
                          enabled: rule.enabled,
                          default_intervals_months: rule.default_intervals_months || [6],
                          default_channel_email: rule.default_channel_email,
                          default_channel_sms: rule.default_channel_sms,
                          allow_override: rule.allow_override
                        })}
                        style={{ fontSize: '0.7rem' }}
                      >
                        {editingRule?.service_id === rule.service_id ? (t.common?.close || 'Close') : (rm.edit || 'Edit')}
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
                    {rm.intervals || 'Intervals'}: {(rule.default_intervals_months || []).join(', ')} {rm.months || 'months'}
                    <span style={{ marginLeft: '1rem' }}>
                      {rule.default_channel_email && 'Email'} {rule.default_channel_sms && '+ SMS'}
                    </span>
                  </div>

                  {/* Edit form */}
                  {editingRule?.service_id === rule.service_id && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: '#0C1220', border: '1px solid rgba(148,163,184,0.1)' }}>
                      <div className="form-check form-switch" style={{ marginBottom: '0.75rem' }}>
                        <input className="form-check-input" type="checkbox" checked={editingRule.enabled} onChange={e => setEditingRule({ ...editingRule, enabled: e.target.checked })} />
                        <label className="form-check-label" style={{ fontSize: '0.85rem' }}>{rm.enabled || 'Enabled'}</label>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ fontSize: '0.75rem', color: '#94A3B8', display: 'block', marginBottom: '0.25rem' }}>{rm.intervals || 'Intervals'} ({rm.months || 'months'}, comma-separated)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={(editingRule.default_intervals_months || []).join(', ')}
                          onChange={e => setEditingRule({ ...editingRule, default_intervals_months: e.target.value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0) })}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem' }}>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={editingRule.default_channel_email} onChange={e => setEditingRule({ ...editingRule, default_channel_email: e.target.checked })} />
                          <label className="form-check-label" style={{ fontSize: '0.85rem' }}>Email</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={editingRule.default_channel_sms} onChange={e => setEditingRule({ ...editingRule, default_channel_sms: e.target.checked })} />
                          <label className="form-check-label" style={{ fontSize: '0.85rem' }}>SMS</label>
                        </div>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => handleSaveRule(editingRule.service_id)}>{rm.saveRule || 'Save Rule'}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setRescheduleModal(null)}>
          <div className="modal-dialog modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-content" style={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                <h5 className="modal-title" style={{ fontSize: '1rem', color: '#D42B2B' }}>{rm.reschedule || 'Reschedule'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setRescheduleModal(null)}></button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: '0.85rem', color: '#94A3B8', marginBottom: '1rem' }}>
                  {rescheduleModal.first_name} {rescheduleModal.last_name} — {rescheduleModal.service_name_fr || rescheduleModal.service_name}
                </div>
                <label style={{ fontSize: '0.75rem', color: '#94A3B8', display: 'block', marginBottom: '0.25rem' }}>{rm.newDate || 'New date'}</label>
                <input type="date" className="form-control form-control-sm" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setRescheduleModal(null)}>{t.common?.close || 'Cancel'}</button>
                <button className="btn btn-primary btn-sm" onClick={handleReschedule} disabled={!rescheduleDate}>{rm.saveReschedule || 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
